import type { ChargebeePaymentIntent } from '@/types/chargebee';
import { readChargebeeErrorText } from './chargebeeErrorText';

/**
 * Chargebee's `validatePaymentIntent` (3DS handler, shared by the card
 * component and both wallet handlers) requires `id && amount && status &&
 * gateway`. The subscription summary only carries `{ id }`, so anything the
 * backend hands us has to pass through `chargebeeService.retrievePaymentIntent`
 * first — otherwise every payment attempt dies with INVALID_PAYMENT_INTENT
 * before a single request reaches the PSP.
 */
export const isHydratedChargebeePaymentIntent = (intent?: ChargebeePaymentIntent): boolean =>
  Boolean(intent?.id && intent.status && intent.gateway && intent.amount !== undefined);

/**
 * A payment intent is SINGLE-USE. Once Chargebee has authorized it, another
 * `authorizeWith3ds` on the same intent is rejected with this code — which is
 * what a shopper hits after any attempt that authorized but never finalized
 * (finalize error, reload, second click). The authorization itself is still
 * good, so the recovery is to finalize with it rather than to re-authorize.
 */
export const isChargebeePaymentIntentAuthorizedError = (error: unknown): boolean =>
  (error as { error_code?: string } | null)?.error_code === 'payment_intent_authorized';

/**
 * Chargebee's wallet docs create the payment intent with the wallet's own
 * `payment_method_type` ("apple_pay"), which is how the module picks its
 * gateway handler and how the authorized intent is recorded. Ours is created
 * once per session and shared by the card form and both wallets, so the wallet
 * declares the type on a COPY: the server-owned object every other consumer
 * holds stays exactly as the backend sent it.
 */
export const withChargebeePaymentMethodType = (
  intent: ChargebeePaymentIntent,
  paymentMethodType: string
): ChargebeePaymentIntent =>
  intent.payment_method_type === paymentMethodType ? intent : { ...intent, payment_method_type: paymentMethodType };

/**
 * The only mount failure that retrying with the untouched session intent could
 * fix: Chargebee refusing the intent itself. Chargebee's own validator takes
 * `payment_method_type` as a free-form optional string, so a rejection of the
 * declared type is a site-level possibility we can't reproduce — never the
 * likely reading of an arbitrary mount error. A missing container, a malformed
 * recurring request or a device without Apple Pay has to surface as itself,
 * otherwise the retry quietly mounts a wallet that authorizes as `card`.
 */
export const isChargebeePaymentIntentRejection = (error: unknown): boolean =>
  /payment_?method_?type|payment.?intent/i.test(readChargebeeErrorText(error));

/** Intent states where the shopper's authorization is already captured. */
export const isChargebeePaymentIntentAuthorized = (intent?: ChargebeePaymentIntent): boolean =>
  intent?.status === 'authorized' || intent?.status === 'consumed';
